import React, { useState, useEffect } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getStoreMetrics } from '@/lib/api';
import { 
  Eye, 
  Package, 
  MessageSquare,
  TrendingUp,
  FileText,
  Send,
  Phone
} from 'lucide-react';

const StoreMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const { isUSA } = useLocale();

  useEffect(() => {
    fetchMetrics();
  }, [period]);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const data = await getStoreMetrics(parseInt(period));
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-8 text-slate-500">
        {isUSA ? 'No metrics available' : 'No hay métricas disponibles'}
      </div>
    );
  }

  const statCards = [
    {
      title: isUSA ? 'Store Views' : 'Visitas a tienda',
      value: metrics.store_views,
      icon: Eye,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: isUSA ? 'Products Viewed' : 'Productos vistos',
      value: metrics.product_views,
      icon: Package,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: isUSA ? 'Inquiries Received' : 'Solicitudes recibidas',
      value: metrics.total_leads,
      icon: MessageSquare,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    }
  ];

  const leadTypes = [
    {
      type: isUSA ? 'Quote requests' : 'Cotizaciones',
      value: metrics.leads_by_type?.quotes || 0,
      icon: FileText,
      color: 'text-purple-600'
    },
    {
      type: isUSA ? 'Sample requests' : 'Muestras',
      value: metrics.leads_by_type?.samples || 0,
      icon: Send,
      color: 'text-pink-600'
    },
    {
      type: isUSA ? 'Contact requests' : 'Contactos',
      value: metrics.leads_by_type?.contacts || 0,
      icon: Phone,
      color: 'text-cyan-600'
    }
  ];

  // Simple bar chart calculation
  const maxViews = Math.max(...(metrics.views_by_day?.map(d => d.views) || [1]), 1);

  return (
    <div className="space-y-6" data-testid="store-metrics">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          {isUSA ? 'Store Analytics' : 'Analíticas de tienda'}
        </h3>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{isUSA ? 'Last 7 days' : 'Últimos 7 días'}</SelectItem>
            <SelectItem value="30">{isUSA ? 'Last 30 days' : 'Últimos 30 días'}</SelectItem>
            <SelectItem value="90">{isUSA ? 'Last 90 days' : 'Últimos 90 días'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat, idx) => (
          <Card key={idx}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Leads Breakdown */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {isUSA ? 'Inquiries by Type' : 'Solicitudes por tipo'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {leadTypes.map((lead, idx) => (
              <div key={idx} className="text-center p-3 bg-slate-50 rounded-lg">
                <lead.icon className={`w-5 h-5 mx-auto mb-1 ${lead.color}`} />
                <p className="text-xl font-bold text-slate-900">{lead.value}</p>
                <p className="text-xs text-slate-500">{lead.type}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Views Chart */}
      {metrics.views_by_day && metrics.views_by_day.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {isUSA ? 'Views Over Time' : 'Visitas en el tiempo'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-32">
              {metrics.views_by_day.slice(-14).map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                    style={{ 
                      height: `${Math.max((day.views / maxViews) * 100, 4)}%`,
                      minHeight: '4px'
                    }}
                    title={`${day.date}: ${day.views} ${isUSA ? 'views' : 'visitas'}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>{metrics.views_by_day[0]?.date?.slice(5)}</span>
              <span>{metrics.views_by_day[metrics.views_by_day.length - 1]?.date?.slice(5)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Products */}
      {metrics.top_products && metrics.top_products.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {isUSA ? 'Most Viewed Products' : 'Productos más vistos'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.top_products.map((product, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-400 w-5">{idx + 1}</span>
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-10 h-10 rounded object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                      <Package className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{product.name}</p>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-slate-500">
                    <Eye className="w-4 h-4" />
                    {product.views}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StoreMetrics;
